import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SearchPanel } from "./components/SearchPanel";
import { PageWidth, ScorePill, StarRow } from "./components/travel-ui";
import { useMarketplaceSearch } from "./context/SearchContext";
import { cheapestFare, MOCK_FLIGHTS } from "./data/mock-flights";
import { MOCK_HOTELS, memberNightlyPrice } from "./data/mock-hotels";
import { formatMoney } from "./lib/format";
import { useMemberAuth } from "./auth/MemberAuthContext";
import { useWallet } from "./services/wallet.service";

export default function MemberHome() {
  const navigate = useNavigate();
  const { setSearch } = useMarketplaceSearch();
  const { session } = useMemberAuth();
  const wallet = useWallet();
  const firstName = (wallet.fullName || session?.fullName || "there").split(" ")[0];
  const cities = Array.from(
    new Map(MOCK_HOTELS.map((hotel) => [hotel.city, hotel])).values(),
  );

  return (
    <div>
      <section className="bg-[#003580] px-4 pb-8 pt-8 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <h1 className="text-3xl font-bold text-white sm:text-4xl">
            Find your next stay or flight
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/80 sm:text-base">
            Member rates, then pay with cash and points.{" "}
            {firstName}, you are {wallet.tierName}.
          </p>
          <div className="mt-6">
            <SearchPanel
              onNavy
              onSearch={(product) => {
                setSearch({ product });
                navigate(product === "stays" ? "/member/hotels" : "/member/flights");
              }}
            />
          </div>
        </div>
      </section>

      <PageWidth className="space-y-10">
        <Link
          to="/member/rewards"
          className="flex flex-col gap-3 rounded-lg border border-[#e7e7e7] bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="text-sm text-[#595959]">Your rewards</p>
            <p className="text-lg font-bold text-[#1a1a1a]">
              {wallet.points.toLocaleString("en-IN")} available
              {wallet.pendingPoints > 0 ? ` · ${wallet.pendingPoints.toLocaleString("en-IN")} pending` : ""}
              {" · "}
              {wallet.tierName}
            </p>
            <p className="text-sm text-[#595959]">
              {wallet.pendingPoints > 0
                ? "Pending points are not spendable until they are released. "
                : ""}
              {wallet.nextTier
                ? `${wallet.remaining.toLocaleString("en-IN")} more to reach ${wallet.nextTier}.`
                : wallet.loaded
                  ? wallet.benefits[0]
                    ? wallet.benefits.slice(0, 2).join(" · ")
                    : "Your points and tier come from your membership."
                  : "Loading your points…"}
            </p>
          </div>
          <span className="inline-flex items-center text-sm font-semibold text-[#006ce4]">
            Open rewards <ArrowRight className="ml-1 h-4 w-4" />
          </span>
        </Link>

        <section>
          <h2 className="text-xl font-bold">Popular destinations</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {cities.map((hotel) => (
              <button
                key={hotel.city}
                type="button"
                className="overflow-hidden rounded-lg bg-white text-left shadow-sm ring-1 ring-black/5"
                onClick={() => {
                  setSearch({ destination: hotel.city, product: "stays" });
                  navigate("/member/hotels");
                }}
              >
                <img src={hotel.image} alt="" className="h-24 w-full object-cover" />
                <span className="block px-3 py-2 text-sm font-semibold">
                  {hotel.city}
                </span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-end justify-between">
            <h2 className="text-xl font-bold">Stays members book</h2>
            <Link to="/member/hotels" className="text-sm font-semibold text-[#006ce4]">
              See all stays
            </Link>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {MOCK_HOTELS.slice(0, 3).map((hotel) => (
              <Link
                key={hotel.id}
                to={`/member/hotels/${hotel.id}`}
                className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5"
              >
                <img src={hotel.image} alt="" className="h-40 w-full object-cover" />
                <div className="space-y-2 p-4">
                  <StarRow count={hotel.stars} />
                  <p className="font-bold">{hotel.name}</p>
                  <p className="text-sm text-[#595959]">{hotel.city}</p>
                  <ScorePill rating={hotel.rating} reviews={hotel.reviews} />
                  <p className="text-sm">
                    <span className="text-lg font-bold">
                      {formatMoney(memberNightlyPrice(hotel), wallet.currency)}
                    </span>{" "}
                    <span className="text-[#595959]">per night</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-end justify-between">
            <h2 className="text-xl font-bold">Flights on popular routes</h2>
            <Link to="/member/flights" className="text-sm font-semibold text-[#006ce4]">
              See all flights
            </Link>
          </div>
          <div className="mt-4 grid gap-3">
            {MOCK_FLIGHTS.slice(0, 3).map((flight) => {
              const fare = cheapestFare(flight, "Economy");
              return (
                <Link
                  key={flight.id}
                  to={`/member/flights/${flight.id}`}
                  className="flex flex-col gap-3 rounded-lg bg-white p-4 shadow-sm ring-1 ring-black/5 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#595959]">
                      {flight.airline} · {flight.number}
                    </p>
                    <p className="text-lg font-bold">
                      {flight.depart} {flight.fromCode}
                      <span className="mx-2 text-sm font-normal text-[#595959]">
                        {flight.duration}
                      </span>
                      {flight.arrive} {flight.toCode}
                    </p>
                    <p className="text-sm text-[#595959]">
                      {flight.fromCity} to {flight.toCity} ·{" "}
                      {flight.stops === 0 ? "Direct" : `${flight.stops} stop`}
                    </p>
                  </div>
                  <p className="text-right">
                    <span className="block text-lg font-bold">
                      {formatMoney(fare.price, wallet.currency)}
                    </span>
                    <span className="text-xs text-[#595959]">per traveller</span>
                  </p>
                </Link>
              );
            })}
          </div>
        </section>
      </PageWidth>
    </div>
  );
}
